import EverTrue from 'app';
import Api from 'entities/helpers/api';
import ErrorLogger from 'entities/helpers/error-log-helper';
import { useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';

const useFileUploader = (attachments = [], drawerId = null) => {
  const [files, setFiles] = useState([]);
  const [isReuploading, setIsReuploading] = useState(false);

  // Save files to sessionStorage whenever they change
  useEffect(() => {
    if (drawerId) {
      const filesKey = `${drawerId}_files`;
      sessionStorage.setItem(filesKey, JSON.stringify(files));
    }
  }, [files, drawerId]);

  // Re-upload existing attachments when component mounts
  useEffect(() => {
    const reuploadExistingAttachments = async () => {
      if (!attachments || attachments.length === 0) return;

      setIsReuploading(true);

      for (const attachment of attachments) {
        try {
          //this triggers when a scheduled email is canceled and moved to drafts
          if (attachment.content) {
            // Convert base64 to blob
            const byteCharacters = atob(attachment.content);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: attachment.content_type });

            // Create a File object from the blob
            const file = new File([blob], attachment.filename, {
              type: attachment.content_type,
              lastModified: Date.now(),
            });

            // Re-upload through the normal process
            await processFileUpload(file, false);
          } else {
            //this triggers when editing a draft that already had attachments
            await processExistingAttachment(attachment);
          }
        } catch (error) {
          console.error('Error re-uploading existing attachment:', error);
          ErrorLogger.error('Error re-uploading existing attachment', {
            extra: { error, filename: attachment.filename, page_name: 'Emailer' },
          });
        }
      }

      setIsReuploading(false);
    };

    reuploadExistingAttachments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  const removeFile = (id) => {
    setFiles(files.filter((f) => f.id !== id));
  };

  const getPresignedUrl = async (name) => {
    return await Api.OUTREACH.UPLOAD_PRESIGNED_URL.get({
      params: {
        fileName: encodeURIComponent(name),
      },
    });
  };

  const uploadFileToAWS = async (presignedUrl, file) => {
    const res = await fetch(presignedUrl, {
      method: 'PUT',
      body: file,
    });
    return res;
  };

  // `key` is the canonical (decoded) S3 object key. It must be percent-encoded
  // before going into the query params: the request-manager builds the URL as
  // `decodeURIComponent(paramify(params))`, so a raw key with spaces/slashes/
  // unicode (e.g. the macOS narrow no-break space) reaches the wire malformed —
  // spaces become `+`, slashes stay bare — and the backend signs a URL for a key
  // that does not exist in S3, failing with "Problems getting the attachment from
  // the provided file URL!". Pre-encoding here mirrors getPresignedUrl and cancels
  // out the request-manager decode, yielding a properly-encoded objectKey.
  const getDownloadUrl = async (key) => {
    return Api.OUTREACH.SIGNED_URL.get({
      params: {
        objectKey: encodeURIComponent(key),
      },
    }).then((res) => res);
  };

  // Handles both the S3 presigned upload URL (bucket.s3.amazonaws.com/KEY?...)
  // and the CloudFront signed download URL (*artifacts.evertrue.com/KEY?...).
  // The API's signed-url endpoint returns CloudFront URLs, so bodies stored in
  // drafts/templates/signatures contain CloudFront hostnames — not S3.
  //
  // The path segment is percent-encoded in the URL (e.g. spaces as %20), so we
  // decode it back to the canonical S3 object key. Every consumer treats this as
  // the raw key: it is persisted as a cadence attachment's content_id and passed
  // to getDownloadUrl, which re-encodes it for the signed-url query. Returning an
  // encoded key here would double-encode it and resolve a nonexistent object.
  const parseKey = (url) => {
    const [withoutQuery] = url.split('?');
    const afterProtocol = withoutQuery.split('://')[1] || withoutQuery;
    const slashIdx = afterProtocol.indexOf('/');
    const encodedKey = slashIdx >= 0 ? afterProtocol.substring(slashIdx + 1) : '';
    try {
      return decodeURIComponent(encodedKey);
    } catch (e) {
      // Malformed percent-encoding — fall back to the raw segment rather than throw.
      return encodedKey;
    }
  };

  /**
   * Scans HTML for S3/CloudFront signed URLs and unconditionally replaces them
   * with fresh ones. The object key is extracted from the URL path to request
   * a new signed URL from the backend.
   */
  const refreshInlineImageUrls = async (htmlBody) => {
    if (!htmlBody) return htmlBody;

    const signedUrlPattern = /https:\/\/[^"'\s)]*(?:s3\.amazonaws\.com|artifacts\.evertrue\.com)\/[^"'\s)]+/g;
    const matches = htmlBody.match(signedUrlPattern);

    if (!matches) return htmlBody;

    const uniqueUrls = [...new Set(matches)];

    const results = await Promise.all(
      uniqueUrls.map(async (oldUrl) => {
        try {
          const key = parseKey(oldUrl);
          const { url: freshUrl } = await getDownloadUrl(key);
          return { oldUrl, freshUrl };
        } catch (error) {
          console.error('Error refreshing inline image URL:', error);
          ErrorLogger.error('Error refreshing inline image URL', {
            extra: { error, page_name: 'Emailer' },
          });
          return null;
        }
      })
    );

    let updatedBody = htmlBody;
    for (const result of results) {
      if (result) {
        updatedBody = updatedBody.replaceAll(result.oldUrl, result.freshUrl);
      }
    }

    return updatedBody;
  };

  const processExistingAttachment = async (attachment) => {
    try {
      // contentId IS the canonical S3 object key
      const { url: downloadUrl } = await getDownloadUrl(attachment.contentId);

      // Fetch real file bytes
      const res = await fetch(downloadUrl);
      const blob = await res.blob();

      const file = new File([blob], attachment.filename, {
        type: attachment.contentType,
        lastModified: Date.now(),
      });

      const newFile = {
        id: attachment.id || uuidv4(),
        file,
        url: downloadUrl,
        name: attachment.filename,
        isInline: attachment.inline || false,
        contentId: attachment.contentId,
        contentDisposition: attachment.contentDisposition || 'attachment',
        originalAttachment: attachment,
      };

      // Dedupe by object key so rehydrating attachments already present in the
      // uploader (e.g. after a save transitions the page into edit mode) can't
      // create a duplicate.
      setFiles((prevFiles) =>
        prevFiles.some((f) => f.contentId === newFile.contentId) ? prevFiles : [...prevFiles, newFile]
      );
    } catch (error) {
      console.error('Error processing existing attachment:', error);
      ErrorLogger.error('Error processing existing attachment', {
        extra: { error, attachment, page_name: 'Emailer' },
      });
      throw error;
    }
  };

  // Rehydrate the uploader from a set of previously-saved attachments
  // (camelCase shape: contentId/filename/contentType/inline/contentDisposition).
  // Used by callers that load attachments after mount — e.g. editing a saved
  // cadence — where the mount-time `attachments` arg isn't yet available.
  const loadAttachments = async (attachmentsToLoad = []) => {
    if (!attachmentsToLoad || attachmentsToLoad.length === 0) return;
    setIsReuploading(true);
    for (const attachment of attachmentsToLoad) {
      try {
        await processExistingAttachment(attachment);
      } catch (error) {
        // processExistingAttachment already logs; keep loading the rest.
      }
    }
    setIsReuploading(false);
  };

  const processFileUpload = async (file, isInline = false) => {
    try {
      const id = uuidv4();

      // Get presigned URL and upload
      const { url } = await getPresignedUrl(file.name);
      const key = parseKey(url);
      await uploadFileToAWS(url, file);
      const { url: downloadUrl } = await getDownloadUrl(key);

      // Create file object with all properties
      const newFile = {
        id,
        file,
        url: downloadUrl,
        name: file.name,
        isInline: isInline,
        contentId: key, // canonical S3 object key
        contentDisposition: isInline ? 'inline' : 'attachment',
      };

      if (!isInline) {
        setFiles((prevFiles) => [...prevFiles, newFile]);
      }

      return { url: downloadUrl, id, name: file.name };
    } catch (e) {
      console.error('Error uploading file to AWS', e);
      ErrorLogger.error('Error uploading file to AWS', { extra: { e, page_name: 'Emailer' } });
      EverTrue.Alert.error('There was a problem uploading your file. Please try again.');

      // Return a rejected promise to properly handle the error
      throw e;
    }
  };

  const onImageUpload = async (file) => {
    return processFileUpload(file, true);
  };

  const onFileUpload = async (file, isInline = false) => {
    return processFileUpload(file, isInline);
  };

  return {
    files,
    setFiles,
    removeFile,
    onFileUpload,
    onImageUpload,
    loadAttachments,
    isReuploading,
    refreshInlineImageUrls,
  };
};

export default useFileUploader;
